<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import { PAGES } from './constants'
import { useNavigation } from './useNavigation'
import { useStudentScreen } from './useStudentScreen'
import type { Student2Context } from '@/types/student2'

const props = defineProps<{ context: Student2Context; absolute: boolean }>()

const { navigationState, getLink, goto, currentUnitId } = useNavigation()
const { isMobileLocal } = useStudentScreen()

const list = useTemplateRef<HTMLElement>('list')
const tooltip = useTemplateRef<HTMLElement>('tooltip')

const hiddenOverride = ref<boolean | null>(null)
const largeUnitId = ref<number | null>(null)
const tooltipUnitId = ref<number | null>(null)
const tooltipText = ref<string | null>(null)

const hidden = computed(() => {
  const hiddenParam = navigationState.params.hidden as boolean
  return hiddenOverride.value === null ? hiddenParam : hiddenOverride.value
})

const floating = computed(() => {
  const tab = navigationState.params.tab
  return PAGES.find(entry => entry.name === tab)?.floatingNav ?? false
})

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

async function animateUnitChange(unitId: number, changeCallback: () => void) {
  if (floating.value && hidden.value) {
    hiddenOverride.value = false
    await delay(700)
  }

  largeUnitId.value = unitId
  await delay(500)
  changeCallback()
  await delay(200)
  largeUnitId.value = null

  await delay(1000)
  hiddenOverride.value = null
}

async function positionTooltip() {
  if (!tooltipUnitId.value || !tooltip.value) return

  const element = document.querySelector(
    `li.student-unit-select__unit[data-unit-id="${tooltipUnitId.value}"]`
  ) as HTMLElement | null

  if (!element) return
  await nextTick()

  const elRect = element.getBoundingClientRect()
  const ttRect = tooltip.value.getBoundingClientRect()
  const isPortrait = window.matchMedia('(orientation: portrait)').matches

  if (isPortrait) {
    tooltip.value.style.right = `${window.innerHeight - elRect.top + 24}px`
    tooltip.value.style.top = `${window.innerWidth - elRect.right + elRect.width / 2 - ttRect.width / 2}px`
  } else {
    tooltip.value.style.right = `${window.innerWidth - elRect.left + 24}px`
    tooltip.value.style.top = `${elRect.top + elRect.height / 2 - ttRect.height / 2}px`
  }

  if (tooltip.value.getBoundingClientRect().top < 0) {
    tooltip.value.style.top = '8px'
  }
}

function isTouch() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

function onOuterTouch(event: Event) {
  const element = document.querySelector('ul.student-unit-select__scroll-list')
  if (element?.contains(event.target as Node)) return
  tooltipUnitId.value = null
}

watch(
  () => props.context,
  async () => {
    await nextTick()
    if (!list.value) return

    const element = list.value.querySelector(`[data-unit-id="${currentUnitId.value}"]`) as HTMLElement | null
    if (!element) return

    const topOffset = element.offsetTop - (list.value.clientHeight - element.offsetHeight) / 2
    list.value.scroll(0, topOffset)
  },
  { immediate: true }
)

watch(tooltipUnitId, value => {
  if (!value) {
    tooltipText.value = null
    return
  }

  positionTooltip()
  const hoverUnit = props.context.course_part.units.find(entry => entry.id === value)
  if (!hoverUnit) return

  if (!hoverUnit.all_tasks_accepted && hoverUnit.all_tasks_completed) {
    tooltipText.value = "Задания из Pupil's book не приняты учителем"
  } else if (!hoverUnit.all_tasks_completed && hoverUnit.all_tasks_accepted) {
    tooltipText.value = "Задания в Pupil's book не выполнены"
  } else {
    tooltipText.value = null
  }
})

onMounted(() => {
  if (isTouch()) {
    document.addEventListener('touchstart', onOuterTouch)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('touchstart', onOuterTouch)
})

defineExpose({ $animateUnitChange: animateUnitChange })
</script>

<template>
  <ul
    class="student-unit-select"
    :class="{
      'student-unit-select_floating': floating,
      'student-unit-select_hidden': floating && hidden,
      'student-unit-select_absolute': absolute
    }"
  >
    <ul ref="list" class="student-unit-select__scroll-list">
      <li class="text-title-bold text-center">Unit</li>
      <li
        v-for="unit in context.course_part.units"
        :key="unit.id"
        class="student-unit-select__unit"
        :data-unit-id="unit.id"
        :class="{
          'student-unit-select__unit_selected': currentUnitId === unit.id,
          'student-unit-select__unit_large': largeUnitId === unit.id,
          'student-unit-select__unit_current': context.smartbook.last_unit_unlocked_by_progress === unit.id
        }"
        @touchstart="
          () => {
            tooltipUnitId = unit.id
          }
        "
        @contextmenu.prevent="
          () => {
            tooltipUnitId = unit.id
          }
        "
        @mouseenter="
          () => {
            tooltipUnitId = unit.id
          }
        "
        @mouseleave="
          () => {
            if (!isTouch()) tooltipUnitId = null
          }
        "
        @scroll="positionTooltip"
      >
        <a @click="() => goto(getLink({ unitId: unit.id }))">
          <transition name="fade">
            <v-icon v-if="unit.all_tasks_completed && unit.all_tasks_accepted" key="3" :size="isMobileLocal ? 48 : 64"
              >student-v2/unit_full</v-icon
            >
            <v-icon v-else-if="unit.all_tasks_accepted" key="2" :size="isMobileLocal ? 48 : 64"
              >student-v2/unit_right_half</v-icon
            >
            <v-icon v-else-if="unit.all_tasks_completed" key="1" :size="isMobileLocal ? 48 : 64"
              >student-v2/unit_left_half</v-icon
            >
            <v-icon v-else key="0" :size="isMobileLocal ? 48 : 64">student-v2/unit_outline</v-icon>
          </transition>
          <span>{{ unit.title.replace('Unit ', '') }}</span>
        </a>
      </li>
    </ul>

    <span
      ref="tooltip"
      class="student-unit-select__unit-tooltip"
      :class="{ 'student-unit-select__unit-tooltip_hidden': !tooltipUnitId || !tooltipText }"
    >
      {{ tooltipText }}
    </span>
  </ul>
</template>

<style lang="scss">
@use '~/styles/pages/student2/screen' as s2;

.student-unit-select {
  order: 2;

  justify-self: center;
  display: flex;
  align-items: center;
  gap: 32px;
  border-radius: 16px;

  background: transparent;
  padding: 0px;

  transition:
    transform 0.3s ease,
    padding 0.3s ease,
    background 0.3s ease,
    box-shadow 0.3s ease;
  transform: translateY(0);

  flex-direction: column;
  width: 88px;
  z-index: 2;
  background: white;
  box-shadow:
    -4px 0px 20px 0px #00000020,
    -2px 0px 4px 0px #0000000a;
  border-radius: 0;

  max-height: 100vh;
  max-height: 100svh;
  @media screen and (orientation: portrait) {
    max-height: 100vw;
    max-height: 100svw;
  }

  @include s2.mobile {
    width: 72px;
  }

  &_hidden {
    transform: translateX(100%);
  }

  &__scroll-list {
    display: flex;
    overflow-x: auto;
    gap: 8px;

    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;

    overflow-y: auto;
    flex-direction: column;
    width: 100%;
    padding: 12px;
  }

  &__unit {
    transition:
      transform 0.3s ease,
      z-index 0.3s ease;

    &,
    & * {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }

    &:first-child {
      transform-origin: center center;
    }
    &:last-child {
      transform-origin: center center;
    }

    &_selected {
      background: $lightenBlue;
      border-radius: 8px;
    }

    &_current .v-icon {
      transform: scale(1.25);
      transition: transform 0.3s ease;

      @include s2.mobile {
        transform: scale(1.4);
      }
    }

    &_large {
      transform: scale(1.4);
      z-index: 2;

      .v-icon {
        transform: scale(1);
      }
    }

    a {
      position: relative;
      width: 64px;
      height: 64px;

      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;

      text-decoration: none;
      color: black;
      font-size: 14px;
      line-height: 16px;
      font-weight: bold;

      @include s2.mobile {
        width: 48px;
        height: 48px;
        font-size: 12px;
        line-height: 14px;
      }

      span {
        z-index: 1;

        display: -webkit-box;
        overflow-wrap: break-word;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        padding: 0 8px;
      }

      .v-icon {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
      }
    }
  }

  &__unit-tooltip {
    pointer-events: none;
    position: fixed;

    background: white;
    box-shadow:
      -1px 1px 4px rgba(58, 70, 93, 0.12),
      -4px 5px 18px 0px rgba(29, 42, 68, 0.13);

    width: 250px;
    padding: 8px;
    border-radius: 8px;

    transition: opacity 0.3s ease;
    opacity: 1;

    &_hidden {
      opacity: 0;
    }
  }
}
</style>
